import { useTranslation } from "react-i18next";
import { Loader2, Pencil } from "lucide-react";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FileUpload } from "@/components/shared/FileUpload";
import { CompanyInvoiceIdentitySection } from "@/components/features/company-image/CompanyInvoiceIdentitySection";
import { JPG_PNG_ACCEPT } from "@/components/features/company-image/invoiceLogoPreview";
import type { InvoiceLogoCropPayload, TenantCurrentResponse } from "@/services/authService";

export interface CompanyImageCardProps {
  tenant: TenantCurrentResponse;
  useSameLogo: boolean;
  onUseSameLogoChange: (value: boolean) => void;
  bothUploading: boolean;
  siteUploading: boolean;
  invoiceUploading: boolean;
  onBothLogoChange: (file: File | null) => void;
  onSiteLogoChange: (file: File | null) => void;
  onInvoiceLogoChange: (file: File | null) => void;
  onSaveBrandColor: (brandColor: string | null) => Promise<void>;
  brandColorSaving: boolean;
  /** Optional preview action; when omitted the preview button is hidden. */
  onPreviewInvoice?: () => void;
  previewPdfLoading?: boolean;
  /**
   * Optional crop action. Omitted by the onboarding surfaces that share this
   * card (accountant client-onboarding, dashboard onboarding), which keeps the
   * crop editor scoped to Settings without forking the component.
   */
  onSaveInvoiceLogoCrop?: (payload: InvoiceLogoCropPayload) => Promise<void>;
  invoiceLogoCropSaving?: boolean;
  /**
   * Upload a newly picked logo together with the crop the user just drew, as
   * one action. Supplying this switches the card to crop-before-upload: the
   * file is held locally until the user confirms, so nothing is committed
   * before they have seen how it will look.
   */
  onUploadInvoiceLogoWithCrop?: (
    file: File,
    target: "both" | "invoice",
    crop: InvoiceLogoCropPayload
  ) => Promise<void>;
  /**
   * Render the invoice half (logo tile, crop, brand colour, preview) inside
   * this card. Default true, which is what the onboarding surfaces want.
   *
   * Settings passes false and renders CompanyInvoiceIdentitySection inside the
   * Marcas list instead: there, the legal entity's invoice identity is the
   * first row of the list, beside the tenant's other marcas rather than in a
   * card of its own. This card then covers only the portal logo.
   */
  showInvoiceSection?: boolean;
  /**
   * Opens the invoice-identity editor (logo on the PDF, crop, brand colour).
   * Only meaningful when showInvoiceSection is false — with the section folded
   * into Marcas, this card would otherwise have no route to those settings for
   * someone who came looking for them here.
   */
  onEditInvoiceIdentity?: () => void;
}

/**
 * Company logo card. Two shapes, one component:
 *
 * - COMBINED (default — the onboarding surfaces): source image, both
 *   destinations, crop, brand colour and preview in one card. One card is the
 *   right amount of ceremony while someone is still setting the tenant up.
 * - SITE ONLY (Settings): just the portal logo. The invoice identity moved
 *   into the Marcas list, because that is what it is — the identity a client
 *   is billed under when no marca applies.
 */
export function CompanyImageCard({
  tenant,
  useSameLogo,
  onUseSameLogoChange,
  bothUploading,
  siteUploading,
  invoiceUploading,
  onSiteLogoChange,
  onInvoiceLogoChange,
  onSaveBrandColor,
  brandColorSaving,
  onPreviewInvoice,
  previewPdfLoading,
  onSaveInvoiceLogoCrop,
  invoiceLogoCropSaving,
  onUploadInvoiceLogoWithCrop,
  showInvoiceSection = true,
  onEditInvoiceIdentity,
}: CompanyImageCardProps): JSX.Element {
  const { t } = useTranslation();
  const anyUploading = bothUploading || siteUploading || invoiceUploading;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("settings.companyImage.title")}</CardTitle>
        {/* Single description. The old layout repeated this same string as the
            card description AND as the uploader's label + helper text. */}
        <CardDescription>
          {showInvoiceSection
            ? t("settings.companyImage.cardHelp")
            : t(
                "settings.companyImage.cardHelpSiteOnly",
                "Se usa en el portal: encabezado y menú lateral."
              )}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {showInvoiceSection ? (
          <CompanyInvoiceIdentitySection
            tenant={tenant}
            portalLogoUrl={tenant.profileImage}
            onSiteLogoChange={onSiteLogoChange}
            siteUploading={siteUploading}
            useSameLogo={useSameLogo}
            onUseSameLogoChange={onUseSameLogoChange}
            bothUploading={bothUploading}
            invoiceUploading={invoiceUploading}
            onInvoiceLogoChange={onInvoiceLogoChange}
            onSaveBrandColor={onSaveBrandColor}
            brandColorSaving={brandColorSaving}
            {...(onPreviewInvoice ? { onPreviewInvoice } : {})}
            {...(previewPdfLoading != null ? { previewPdfLoading } : {})}
            {...(onSaveInvoiceLogoCrop ? { onSaveInvoiceLogoCrop } : {})}
            {...(invoiceLogoCropSaving != null ? { invoiceLogoCropSaving } : {})}
            {...(onUploadInvoiceLogoWithCrop
              ? { onUploadInvoiceLogoWithCrop }
              : {})}
          />
        ) : (
          <>
            {/* Plain site-logo upload, no crop step: the crop belongs to the
                INVOICE image, which is chosen in the Marcas list. When the
                invoice shares this image, replacing it here changes both, and
                the crop is adjusted from that row. */}
            <FileUpload
              key={`source-${tenant.profileImage ?? "default"}`}
              currentImageUrl={tenant.profileImage}
              onFileChange={onSiteLogoChange}
              disabled={anyUploading}
              showLabel={false}
              acceptedMimeTypes={JPG_PNG_ACCEPT}
              actions={
                onEditInvoiceIdentity ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={onEditInvoiceIdentity}
                  >
                    <Pencil className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                    {t("common.edit", "Editar")}
                  </Button>
                ) : undefined
              }
            />
            {siteUploading && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>{t("settings.companyImage.uploading")}</span>
              </div>
            )}
            {/* No portal preview tile here. It existed to CONTRAST the portal
                logo with the invoice one; with the invoice half moved to
                Marcas there is nothing to contrast, and the tile just repeated
                the card's own subtitle under a second copy of the image the
                uploader is already showing. The onboarding card still renders
                it, where both destinations sit side by side. */}
          </>
        )}
      </CardContent>
    </Card>
  );
}
